class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.thumbs = Array.from(this.querySelectorAll('.v3-thumb'));
    this.items = Array.from(this.querySelectorAll('.v3-gallery__item'));
    this.rail = this.querySelector('.v3-gallery__rail');
    this.thumbsEl = this.querySelector('.v3-thumbs');
    this.arrowUp = this.querySelector('.v3-thumb-arrow--up');
    this.arrowDown = this.querySelector('.v3-thumb-arrow--down');
    this.prevBtn = this.querySelector('.v3-main-nav--prev');
    this.nextBtn = this.querySelector('.v3-main-nav--next');
    this.dotsEl = this.querySelector('.v3-dots');
    this.dotsRowEl = this.querySelector('.v3-dots-row');
    this._dots = this.dotsRowEl ? Array.from(this.dotsRowEl.querySelectorAll('.v3-dot')) : [];
    this._current = 0;

    this.thumbs.forEach(t => t.addEventListener('click', () => this._goTo(Number(t.dataset.mediaIndex))));

    if (this.arrowUp) this.arrowUp.addEventListener('click', () => this._scrollThumbs(-1));
    if (this.arrowDown) this.arrowDown.addEventListener('click', () => this._scrollThumbs(1));
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._step(1));

    if (this.rail) this.rail.addEventListener('scroll', this._onScroll.bind(this), { passive: true });
    if (this.thumbsEl) this.thumbsEl.addEventListener('scroll', () => this._updateArrows(), { passive: true });
    this._dots.forEach(d => d.addEventListener('click', () => this._goTo(Number(d.dataset.index))));
    this._DOT_STEP = 12 + 10; // dot width + gap
    this._DOT_MAX = 5;
    document.addEventListener('product:variant:change', e => {
      const mid = e.detail && e.detail.featuredMediaId;
      if (!mid) return;
      const item = this.items.find(el => el.dataset.mediaId == mid);
      if (item) this._goTo(Number(item.dataset.mediaIndex));
    });

    this._updateArrows();
    this._updateDots(0);
  }

  _goTo(i) {
    this._current = i;
    this.thumbs.forEach((t, idx) => t.classList.toggle('active', idx === i));
    this.items.forEach((el, idx) => el.classList.toggle('is-active', idx === i));
    if (window.innerWidth < 990 && this.rail && this.items[i]) {
      this.rail.scrollTo({ left: this.items[i].offsetLeft, behavior: 'smooth' });
    }
    this._scrollThumbIntoView(i);
    this._updateMainNavBtns();
    this._updateDots(i);
  }

  _step(dir) {
    const next = Math.max(0, Math.min(this.items.length - 1, this._current + dir));
    this._goTo(next);
  }

  _scrollThumbs(dir) {
    if (!this.thumbsEl) return;
    const thumbEl = this.thumbsEl.querySelector('.v3-thumb');
    const amount = thumbEl ? (thumbEl.offsetHeight + 6) * 2 : 120;
    this.thumbsEl.scrollBy({ top: dir * amount, behavior: 'smooth' });
  }

  _scrollThumbIntoView(i) {
    if (!this.thumbsEl || window.innerWidth < 990) return;
    const thumb = this.thumbs[i];
    if (!thumb) return;
    const wrapH = this.thumbsEl.clientHeight;
    const thumbTop = thumb.offsetTop;
    const thumbH = thumb.offsetHeight;
    const scrollTop = this.thumbsEl.scrollTop;
    if (thumbTop < scrollTop) {
      this.thumbsEl.scrollTo({ top: thumbTop, behavior: 'smooth' });
    } else if (thumbTop + thumbH > scrollTop + wrapH) {
      this.thumbsEl.scrollTo({ top: thumbTop + thumbH - wrapH, behavior: 'smooth' });
    }
  }

  _updateArrows() {
    if (!this.thumbsEl || window.innerWidth < 990) return;
    const { scrollTop, scrollHeight, clientHeight } = this.thumbsEl;
    if (this.arrowUp) this.arrowUp.classList.toggle('is-hidden', scrollTop <= 0);
    if (this.arrowDown) this.arrowDown.classList.toggle('is-hidden', scrollTop + clientHeight >= scrollHeight - 1);
  }

  _updateMainNavBtns() {
    if (this.prevBtn) this.prevBtn.classList.toggle('is-hidden', this._current === 0);
    if (this.nextBtn) this.nextBtn.classList.toggle('is-hidden', this._current >= this.items.length - 1);
  }

  _updateDots(i) {
    if (!this._dots.length || !this.dotsEl || !this.dotsRowEl) return;
    const total = this._dots.length;
    const MAX = this._DOT_MAX;
    const STEP = this._DOT_STEP;

    if (total <= MAX) {
      // Few dots: no clipping needed, just center naturally
      this.dotsEl.classList.add('is-few');
      this.dotsEl.style.width = '';
      this.dotsRowEl.style.transform = '';
      this._dots.forEach((d, idx) => {
        d.classList.toggle('is-active', idx === i);
        d.classList.remove('is-small');
      });
      return;
    }

    // Many dots: clip wrapper + sliding translateX
    const trackWidth = MAX * 12 + (MAX - 1) * 10; // 100px for 5 dots
    this.dotsEl.classList.remove('is-few');
    this.dotsEl.style.width = trackWidth + 'px';

    // Window: keep active near center
    let start = Math.max(0, Math.min(i - Math.floor(MAX / 2), total - MAX));
    const end = start + MAX;

    // Slide row so first visible dot is at position 0 inside the track
    this.dotsRowEl.style.transform = `translateX(-${start * STEP}px)`;

    this._dots.forEach((d, idx) => {
      const inWindow = idx >= start && idx < end;
      const isEdge = inWindow && (
        (idx === start && start > 0) ||
        (idx === end - 1 && end < total)
      );
      d.classList.toggle('is-active', idx === i);
      d.classList.toggle('is-small', isEdge);
    });
  }

  _onScroll() {
    if (window.innerWidth >= 990) return;
    const idx = Math.round(this.rail.scrollLeft / this.rail.clientWidth);
    this._current = idx;
    this.thumbs.forEach((t, i) => t.classList.toggle('active', i === idx));
    this.items.forEach((el, i) => el.classList.toggle('is-active', i === idx));
    this._updateDots(idx);
  }
}
customElements.define('product-gallery', ProductGallery);

class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.mq = window.matchMedia('(min-width: 990px)');
    this.barBtns = Array.from(this.querySelectorAll('.v3-tab--bar'));
    this.accBtns = Array.from(this.querySelectorAll('.v3-tab--accordion'));
    this.panels = Array.from(this.querySelectorAll('.v3-tab-panel'));
    this.barBtns.forEach((b) => {
      b.addEventListener('click', () => this._onClick(Number(b.dataset.tabIndex)));
    });
    this.accBtns.forEach((b) => {
      b.addEventListener('click', () => this._onClick(Number(b.dataset.tabIndex)));
    });
    this._onMqChange = () => this._applyMode();
    this.mq.addEventListener('change', this._onMqChange);
    this._applyMode();
  }

  disconnectedCallback() {
    this.mq?.removeEventListener('change', this._onMqChange);
  }

  _isDesktop() {
    return this.mq.matches;
  }

  _closeAll() {
    this.barBtns.forEach((b) => {
      b.classList.remove('is-active');
      b.setAttribute('aria-selected', 'false');
    });
    this.accBtns.forEach((b) => {
      b.classList.remove('is-active');
      b.setAttribute('aria-expanded', 'false');
    });
    this.panels.forEach((p) => p.setAttribute('hidden', ''));
  }

  _activate(i) {
    this.barBtns.forEach((b, j) => {
      const active = j === i;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.accBtns.forEach((b, j) => {
      const active = j === i;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-expanded', active ? 'true' : 'false');
    });
    this.panels.forEach((p, j) => {
      if (j === i) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
  }

  _applyMode() {
    if (this.panels.length > 0) this._activate(0);
    else this._closeAll();
  }

  _onClick(i) {
    const btn = this._isDesktop() ? this.barBtns[i] : this.accBtns[i];
    if (btn?.classList.contains('is-active')) this._closeAll();
    else this._activate(i);
  }
}
customElements.define('product-tabs', ProductTabs);
